/* =========================================================
  TF / DAWN-STYLE CART DRAWER + CART PAGE SYNC (ALL-IN-ONE)
  - Sync 2 chiều: Cart Page <-> Cart Drawer
  - Event: tf:cart:updated { detail: { cart } }
  - Drawer refresh bằng sections=cart-drawer (giống Dawn)
  - Update bubble count
  - Quantity +/- remove clear
  - CartNote (debounce)
  - Click overlay close
========================================================= */

(() => {
  /* -----------------------
   * Guards / config
   * --------------------- */
  window.__TF_CART_DIRTY__ = false;

  if (window.__TF_CART_ALL_IN_ONE__) return;
  window.__TF_CART_ALL_IN_ONE__ = true;

  const ROOT_URL = window.routes?.root_url || "/";
  const routes = window.routes || {
    cart_url: "/cart",
    cart_add_url: "/cart/add.js",
    cart_change_url: "/cart/change.js",
    cart_update_url: "/cart/update.js",
  };

  const ON_CHANGE_DEBOUNCE_TIMER = 450;

  const debounce = (fn, wait = 250) => {
    let t = null;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const fetchConfig = () => ({
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
  });

  const setBusy = (el, busy) => {
    if (!el) return;
    el.style.pointerEvents = busy ? "none" : "";
    el.style.opacity = busy ? "0.6" : "";
    el.setAttribute("aria-busy", busy ? "true" : "false");
  };

  const safeJSON = async (res) => {
    try {
      return await res.json();
    } catch (_) {
      return {};
    }
  };

  /* -----------------------
   * Cart JSON + Event bus
   * --------------------- */
  async function getCart() {
    const res = await fetch(`${ROOT_URL}cart.js`, { credentials: "same-origin" });
    if (!res.ok) throw new Error("GET cart.js failed");
    return res.json();
  }

  function emitCartUpdated(cart) {
    try {
      window.dispatchEvent(new CustomEvent("tf:cart:updated", { detail: { cart } }));
    } catch (_) {}
  }

  /* -----------------------
   * Update bubble count
   * --------------------- */
  function patchCartBubble(cart) {
    const bubbleWrap =
      document.querySelector("#cart-icon-bubble .cart-count-bubble") ||
      document.querySelector(".cart-count-bubble");

    const bubble =
      bubbleWrap?.querySelector("span") ||
      document.querySelector("#cart-icon-bubble .cart-qty") ||
      document.querySelector("#cart-icon-bubble .qty");

    if (!bubbleWrap || !bubble) return;

    const count = Number(cart?.item_count || 0);

    if (count > 0) {
      // bubbleWrap.style.display = "";
      bubble.textContent = String(count);
      bubble.setAttribute("aria-label", String(count));
    } else {
      // bubbleWrap.style.display = "none";
      bubble.textContent = "0";
      bubble.setAttribute("aria-label", "0");
    }
  }

  window.updateCartCount = async function () {
    try {
      const cart = await getCart();
      patchCartBubble(cart);
    } catch (e) {
      console.error(e);
    }
  };

  /* =========================================================
   * CART NOTE (DAWN-LIKE)
   * ======================================================= */
  if (!customElements.get("cart-note")) {
    customElements.define(
      "cart-note",
      class CartNote extends HTMLElement {
        constructor() {
          super();
          this.addEventListener(
            "input",
            debounce(async (event) => {
              const body = JSON.stringify({ note: event.target.value });
              await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body });
              // sync everywhere
              const cart = await getCart().catch(() => null);
              if (cart) emitCartUpdated(cart);
            }, ON_CHANGE_DEBOUNCE_TIMER)
          );
        }
      }
    );
  }

  /* =========================================================
   * CART DRAWER (your implementation, keep behavior)
   * ======================================================= */
  document.addEventListener("click", (e) => {
    const drawer = document.querySelector("cart-drawer.active");
    if (!drawer) return;

    const overlay = drawer.querySelector("#CartDrawer-Overlay");
    if (!overlay) return;

    // click on overlay -> close
    if (overlay.contains(e.target)) {
      drawer.close();
    }
  });

  class CartDrawer extends HTMLElement {
    constructor() {
      super();

      this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());
      this.setHeaderCartIconAccessibility();

      this.animateProgress = this.animateProgress.bind(this);
    }

    getProgressEl() {
      return (
        this.querySelector(".tf-progress-bar .value") ||
        this.querySelector(".progress-cart .value") ||
        null
      );
    }

    animateProgress(delay = 600) {
      const valueEl = this.getProgressEl();
      if (!valueEl) return;

      const progressValue = Number(valueEl.dataset.progress || 0);
      window.setTimeout(() => {
        valueEl.style.width = `${Math.max(0, Math.min(100, progressValue))}%`;
      }, delay);
    }

    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector("#cart-icon-bubble");
      if (!cartLink) return;

      cartLink.setAttribute("role", "button");
      cartLink.setAttribute("aria-haspopup", "dialog");

      cartLink.addEventListener("click", async (event) => {
        event.preventDefault();

        if (window.__TF_CART_DIRTY__) {
          try {
            await CartDrawerAPI.refreshDrawerSection({ openDrawer: false });
            window.__TF_CART_DIRTY__ = false;
          } catch (e) {
            console.error("[TF Drawer] pre-open refresh failed", e);
          }
        }

        this.open(cartLink);
      });

      cartLink.addEventListener("keydown", (event) => {
        if (event.code.toUpperCase() === "SPACE") {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);

      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) this.setSummaryAccessibility(cartDrawerNote);

      this.animateProgress(600);

      setTimeout(() => {
        this.classList.add("animate", "active");
      });

      this.addEventListener(
        "transitionend",
        () => {
          // trapFocus(...) if you use
        },
        { once: true }
      );

      document.body.classList.add("overflow-hidden");
    }

    close() {
      this.classList.remove("active");
      document.body.classList.remove("overflow-hidden");
    }

    setSummaryAccessibility(cartDrawerNote) {
      cartDrawerNote.setAttribute("role", "button");
      cartDrawerNote.setAttribute("aria-expanded", "false");

      if (cartDrawerNote.nextElementSibling?.getAttribute("id")) {
        cartDrawerNote.setAttribute("aria-controls", cartDrawerNote.nextElementSibling.id);
      }

      cartDrawerNote.addEventListener("click", (event) => {
        event.currentTarget.setAttribute(
          "aria-expanded",
          !event.currentTarget.closest("details")?.hasAttribute("open")
        );
      });
    }

    renderContents(parsedState) {
      const popup = this.querySelector(".popup-shopping-cart");
      if (popup?.classList.contains("is-empty")) popup.classList.remove("is-empty");

      this.productId = parsedState.id;

      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;

        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      });

      requestAnimationFrame(() => {
        this.animateProgress(200);
      });

      setTimeout(() => {
        this.open();
      });
    }

    getSectionInnerHTML(html, selector = ".shopify-section") {
      return new DOMParser().parseFromString(html, "text/html").querySelector(selector)?.innerHTML || "";
    }

    getSectionsToRender() {
      return [
        { id: "cart-drawer", selector: "#CartDrawer" },
        { id: "cart-icon-bubble" },
      ];
    }

    setActiveElement(element) {
      this.activeElement = element;
    }
  }

  if (!customElements.get("cart-drawer")) {
    customElements.define("cart-drawer", CartDrawer);
  }

  /* =========================================================
   * CartDrawerAPI (single source of truth)
   * ======================================================= */
  const CartDrawerAPI = (() => {
    async function postJSON(url, data) {
      const res = await fetch(url, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: data ? JSON.stringify(data) : undefined,
      });
      if (!res.ok) throw new Error(`Request failed: ${url}`);
      return safeJSON(res);
    }

    async function refreshDrawerSection({ openDrawer = true } = {}) {
      const url = `${ROOT_URL}?sections=cart-drawer`;
      const res = await fetch(url, { credentials: "same-origin", headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error("Failed to fetch cart-drawer section");

      const sections = await res.json();
      const html = sections["cart-drawer"];
      if (!html) throw new Error("Missing sections['cart-drawer']");

      const doc = new DOMParser().parseFromString(html, "text/html");

      // ✅ replace WHOLE cart-drawer content (ổn định nhất)
      const newDrawerEl = doc.querySelector("cart-drawer");
      const curDrawerEl = document.querySelector("cart-drawer");

      if (newDrawerEl && curDrawerEl) {
        curDrawerEl.innerHTML = newDrawerEl.innerHTML;
      } else {
        // fallback: replace #CartDrawer nếu không tìm thấy cart-drawer
        const newInner = doc.querySelector("#CartDrawer");
        const curInner = document.querySelector("#CartDrawer");
        if (newInner && curInner) curInner.innerHTML = newInner.innerHTML;
      }

      // update bubble
      await window.updateCartCount?.();

      // ✅ mở lại drawer nếu đang dùng open
      if (openDrawer) document.querySelector("cart-drawer")?.open?.();
    }


    async function changeLine(line, quantity, { openDrawer = true } = {}) {
      await postJSON(`${ROOT_URL}cart/change.js`, { line, quantity });
      await refreshDrawerSection({ openDrawer });

      const cart = await getCart().catch(() => null);
      if (cart) emitCartUpdated(cart);
    }

    async function clearCart({ openDrawer = true } = {}) {
      await fetch(`${ROOT_URL}cart/clear.js`, {
        method: "POST",
        credentials: "same-origin",
      });
      await refreshDrawerSection({ openDrawer });

      const cart = await getCart().catch(() => null);
      if (cart) emitCartUpdated(cart);
    }

    async function addItem(variantId, quantity = 1, { openDrawer = true } = {}) {
      await postJSON(`${ROOT_URL}cart/add.js`, { id: variantId, quantity });
      await refreshDrawerSection({ openDrawer });

      const cart = await getCart().catch(() => null);
      if (cart) emitCartUpdated(cart);
    }

    return { changeLine, clearCart, refreshDrawerSection, addItem };
  })();

  // expose for other scripts if needed
  window.CartDrawerAPI = CartDrawerAPI;

  /* =========================================================
   * Drawer buttons/components
   * ======================================================= */

  // EMPTY CART
  if (!customElements.get("cart-drawer-clear")) {
    customElements.define(
      "cart-drawer-clear",
      class CartDrawerClear extends HTMLElement {
        constructor() {
          super();
          this.addEventListener("click", this.onClick.bind(this));
        }
        async onClick(event) {
          const clearBtn = event.target.closest("[data-clear-cart]");
          if (!clearBtn) return;

          event.preventDefault();
          try {
            setBusy(clearBtn, true);
            await CartDrawerAPI.clearCart({ openDrawer: true });
          } catch (e) {
            console.error(e);
          } finally {
            setBusy(clearBtn, false);
          }
        }
      }
    );
  }

  // REMOVE LINE
  if (!customElements.get("cart-remove-button")) {
    customElements.define(
      "cart-remove-button",
      class CartRemoveButton extends HTMLElement {
        constructor() {
          super();
          this.addEventListener("click", this.onClick.bind(this));
        }

        async onClick(event) {
          const btn = event.target.closest("button");
          if (!btn) return;

          event.preventDefault();

          const line = Number(this.dataset.index);
          if (!line) return;

          try {
            setBusy(btn, true);
            await CartDrawerAPI.changeLine(line, 0, { openDrawer: true });
          } catch (e) {
            console.error(e);
          } finally {
            setBusy(btn, false);
          }
        }
      }
    );
  }

  // QUANTITY +/- + INPUT CHANGE
  if (!customElements.get("quantity-input")) {
    customElements.define(
      "quantity-input",
      class QuantityInput extends HTMLElement {
        constructor() {
          super();

          this.input = this.querySelector("input.quantity-product[data-index]");
          if (!this.input) return;

          this.addEventListener("click", this.onClick.bind(this));
          this.input.addEventListener("change", this.onChange.bind(this));
        }

        get line() {
          return Number(this.input?.dataset?.index || 0);
        }

        get value() {
          return Number(this.input?.value || 0);
        }

        async onClick(event) {
          const plus = event.target.closest(".plus-quantity");
          const minus = event.target.closest(".minus-quantity");
          if (!plus && !minus) return;

          event.preventDefault();
          if (!this.line) return;

          const next = Math.max(0, this.value + (plus ? 1 : -1));
          this.input.value = String(next); // optimistic

          try {
            setBusy(this, true);
            await CartDrawerAPI.changeLine(this.line, next, { openDrawer: true });
          } catch (e) {
            console.error(e);
          } finally {
            setBusy(this, false);
          }
        }

        async onChange() {
          if (!this.line) return;

          let next = Number(this.input.value || 0);
          if (Number.isNaN(next) || next < 0) next = 0;
          this.input.value = String(next);

          try {
            setBusy(this, true);
            await CartDrawerAPI.changeLine(this.line, next, { openDrawer: true });
          } catch (e) {
            console.error(e);
          } finally {
            setBusy(this, false);
          }
        }
      }
    );
  }

  /* =========================================================
   * ProductActions (Add to cart -> use API)
   * ======================================================= */
class ProductActions extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.addEventListener("click", this.onClick);
  }

  _routes() {
    const r = window.routes || {};
    return {
      cart_add_url: r.cart_add_url || "/cart/add.js",
      cart_url: r.cart_url || "/cart",
      cart_js_url: r.cart_js_url || "/cart.js",
    };
  }

  _setBtnLoading(btn, on) {
    if (!btn) return;
    btn.classList.toggle("is-loading", !!on);
    btn.toggleAttribute("disabled", !!on);
    btn.setAttribute("aria-busy", on ? "true" : "false");
  }

  /* ---- Add-to-cart success feedback ----
     Briefly swaps the cart icon for a checkmark and the tooltip text to
     "Added to bag" so the person gets a clear, premium confirmation right
     where they clicked, instead of only relying on the drawer opening. */
  _showAddedFeedback(btn) {
    if (!btn) return;

    const tooltip = btn.querySelector(".tooltip");
    if (tooltip && btn.dataset.tfOriginalTooltip === undefined) {
      btn.dataset.tfOriginalTooltip = tooltip.textContent;
    }

    btn.classList.add("is-added");
    if (tooltip) tooltip.textContent = "Added to bag";

    clearTimeout(btn.__tfAddedTimer);
    btn.__tfAddedTimer = setTimeout(() => {
      btn.classList.remove("is-added");
      if (tooltip && btn.dataset.tfOriginalTooltip !== undefined) {
        tooltip.textContent = btn.dataset.tfOriginalTooltip;
      }
    }, 1800);
  }

  async onClick(event) {
    const addBtn = event.target.closest("[data-add-to-cart]");
    const quickBtn = event.target.closest("[data-quick-add]");

    if (addBtn) {
      event.preventDefault();
      const variantId = addBtn.dataset.variantId;
      if (!variantId) return;

      await this.addToCart(variantId, 1, addBtn);
      return;
    }

    if (quickBtn) {
      event.preventDefault();
      const handle = quickBtn.dataset.productHandle;
      if (!handle) return;

      // Quick Add = instant add-to-cart. No variant-picker popup: we grab
      // the product's first AVAILABLE variant (falling back to the very
      // first variant if none are in stock) and add it straight to the
      // cart, same as a simple product's [data-add-to-cart] button.
      await this.quickAddDirect(handle, quickBtn);
    }
  }

  async quickAddDirect(handle, btn) {
    this._setBtnLoading(btn, true);

    try {
      const res = await fetch(`/products/${handle}.js`, { credentials: "same-origin" });
      if (!res.ok) throw new Error("Failed to load product data");

      const product = await res.json();
      if (!product?.variants?.length) return;

      const variant = product.variants.find((v) => v.available) || product.variants[0];
      if (!variant) return;

      await this.addToCart(variant.id, 1, btn);
    } catch (e) {
      console.error(e);
    } finally {
      this._setBtnLoading(btn, false);
    }
  }

  async addToCart(variantId, quantity, btn) {
    this._setBtnLoading(btn, true);

    try {
      const routes = this._routes();

      const res = await fetch(routes.cart_add_url, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: variantId, quantity }),
      });

      if (!res.ok) throw new Error("Add to cart failed");

      await Promise.allSettled([this.updateCartDrawer(), this.updateCartCount()]);

      this._showAddedFeedback(btn);
    } catch (error) {
      console.error(error);
    } finally {
      this._setBtnLoading(btn, false);
    }
  }

  updateCartDrawer() {
    const cartDrawerEl = document.querySelector("cart-drawer");
    if (!cartDrawerEl) return Promise.resolve();

    const routes = this._routes();

    return fetch(`${routes.cart_url}?section_id=cart-drawer`, { credentials: "same-origin" })
      .then((res) => res.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const newDrawer = doc.querySelector("cart-drawer");
        if (!newDrawer) return;

        cartDrawerEl.innerHTML = newDrawer.innerHTML;
        cartDrawerEl.open?.();
      })
      .catch(console.error);
  }

  updateCartCount() {
    const routes = this._routes();

    return fetch(routes.cart_js_url, { credentials: "same-origin" })
      .then((res) => res.json())
      .then((cart) => {
        const bubbleWrap = document.querySelector(".cart-count-bubble");
        const bubble =
          bubbleWrap?.querySelector("span") ||
          document.querySelector("#cart-icon-bubble .cart-qty") ||
          document.querySelector("#cart-icon-bubble .qty");

        if (!bubbleWrap || !bubble) return;

        const count = Number(cart?.item_count || 0);
        bubble.textContent = String(count);
        bubbleWrap.style.display = count > 0 ? "" : "none";
      })
      .catch(console.error);
  }
}

// ✅ tránh lỗi define 2 lần
if (!customElements.get("product-actions")) {
  customElements.define("product-actions", ProductActions);
}

  /* =========================================================
   * SYNC: Cart Page -> Drawer (when drawer open)
   * ======================================================= */
  (() => {
    if (window.__TF_DRAWER_SYNC_BOUND__) return;
    window.__TF_DRAWER_SYNC_BOUND__ = true;

    let timer = null;

    const isDrawerOpen = () => {
      const drawer = document.querySelector("cart-drawer");
      if (!drawer) return false;
      if (drawer.classList.contains("active")) return true;
      if (drawer.hasAttribute("open")) return true;
      if (drawer.getAttribute("aria-hidden") === "false") return true;
      return false;
    };

    window.addEventListener("tf:cart:updated", async (e) => {
      const cart = e.detail?.cart;
      if (cart) patchCartBubble(cart);

      // cart changed while drawer closed -> mark dirty
      if (!isDrawerOpen()) {
        window.__TF_CART_DIRTY__ = true;
        return;
      }

      clearTimeout(timer);
      timer = setTimeout(async () => {
        try {
          await CartDrawerAPI.refreshDrawerSection({ openDrawer: true });
          window.__TF_CART_DIRTY__ = false;
        } catch (err) {
          console.error("[TF Drawer Sync] refresh failed", err);
        }
      }, 80);
    });
  })();

  /* =========================================================
   * INIT bubble on load
   * ======================================================= */
  window.updateCartCount?.();
})();
